import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DraftSummary, OnboardJob, Ownership } from '../api.js';
import type { DashboardActions, DashboardState, OnboardState } from '../store.js';
import { readCrawlOutcome } from '../joblog.js';
import { Empty, ErrorNote, Pill, Scroller, When } from '../ui/bits.js';
import { CrawlRefusals, GateLedger } from '../ui/outcome.js';

/**
 * Add a site: paste a URL, watch the crawl, review the draft, publish.
 *
 * It is reached at `#/crawl` from the button on the right of the tab strip and
 * from the Sites empty state, rather than from a permanent tab of its own: a
 * customer crawls once per site and an operator crawls on somebody's behalf
 * occasionally, and that is not worth a fifth of the navigation. The route
 * survives — a crawl in flight has to outlive a reload, which is why the job id
 * is in the hash — and `#/onboard` still parses, for the links already sent.
 *
 * The public landing console does the same job for a stranger; this screen
 * exists because a person who has SIGNED IN had no way to start a crawl that
 * the server would tie to their account. `POST /api/onboard` records the
 * session's account on the job, and that record is what later lets the same
 * person publish it without the operator's secret. Starting the crawl from the
 * dashboard is what makes the cookie travel with it.
 *
 * Three rules, the same ones every other screen keeps.
 *
 * THE SERVER'S SENTENCE IS THE ANSWER. A refusal from `/publish` — a
 * third-party draft an account may not publish, a self-owned draft with no
 * verified domain, a job that is not yours — is printed as it arrived, code and
 * all. This screen adds the next step beside it and never a softer paraphrase,
 * because the server's sentence was written to say what to do.
 *
 * NOTHING IS LIVE UNTIL IT SAYS SO. A finished crawl is a draft, and the screen
 * says "draft" until the publish route answers with the URLs the tenant now
 * serves on. A progress bar reaching 100% is the end of a crawl, not of the
 * story, and the wording keys on the record's own `draft` flag.
 *
 * WHAT WAS REFUSED IS PART OF THE RESULT. A table of what shipped, with no
 * statement of what did not, is how a customer comes to open a site and find
 * three tools where the crawl proposed twelve. The gate's verdicts, the tools
 * the crawl scored below its usefulness threshold and shipped switched off, and
 * the pages it ran out of budget before fetching are all in the record this
 * screen is already polling — see `dashboard/joblog.ts` — so they are rendered
 * under the draft rather than left to whoever happened to watch the other
 * console's stream.
 *
 * WHAT THE OWNERSHIP CHOICE COSTS is stated on the form, before anything is
 * queued. It is an assertion about legal standing, not a rendering option: it
 * changes the manifest the crawler assembles, and the server will ask for the
 * DNS record behind it at publish time.
 */

const OWNERSHIP_LABEL: Record<string, string> = {
  'third-party': 'someone else’s site',
  'self-owned': 'my own site',
  permitted: 'permitted by the brand',
};

function ownershipWord(value: string): string {
  return OWNERSHIP_LABEL[value] ?? value;
}

function stateTone(state: OnboardJob['state'], published: boolean): 'plain' | 'warn' | 'good' {
  if (published) return 'good';
  if (state === 'failed' || state === 'cancelled') return 'warn';
  if (state === 'succeeded') return 'good';
  return 'plain';
}

/** The record's own words for where it is, with `draft` deciding "ready" versus "live". */
function stateWord(job: OnboardJob): string {
  if (job.publishedAt) return 'published';
  if (job.draft) return 'draft — ready to publish';
  return job.state;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/* ── The form ──────────────────────────────────────────────────────────── */

function Choice(props: {
  value: Ownership;
  current: Ownership;
  head: string;
  body: VNode;
  onPick: (value: Ownership) => void;
}): VNode {
  const on = props.value === props.current;
  return html`
    <label class=${on ? 'choice choice--on' : 'choice'}>
      <input
        type="radio"
        name="onboard-ownership"
        class="choice__radio"
        value=${props.value}
        checked=${on}
        onChange=${() => props.onPick(props.value)}
      />
      <span class="choice__text">
        <b class="choice__head">${props.head}</b>
        <span class="choice__body">${props.body}</span>
      </span>
    </label>
  `;
}

/**
 * Exported because the first run needs it INLINE.
 *
 * A brand-new account lands on Sites with nothing to look at, and the one
 * action that changes that was a link to another screen. A link is a question
 * ("do I want to do this?") where a field is an invitation, so the same form
 * renders there — the same state block, the same submit, the same navigation to
 * `#/crawl/<jobId>` when the server answers. There is deliberately no second
 * copy of it: two crawl forms would drift, and the ownership radio is the one
 * question on this screen whose wording is a legal assertion.
 */
export function CrawlForm(props: { state: DashboardState; actions: DashboardActions }): VNode {
  const { state, actions } = props;
  const ob = state.onboard;
  const operator = state.principal?.kind === 'operator';

  return html`
    <form
      class="onb"
      onSubmit=${(e: Event) => {
        e.preventDefault();
        actions.startOnboard();
      }}
    >
      <label class="unlock__label" for="onboard-url">Site address</label>
      <div class="onb__row">
        <input
          id="onboard-url"
          class="unlock__input onb__input"
          type="url"
          inputmode="url"
          autocomplete="url"
          spellcheck="false"
          placeholder="https://example.com"
          value=${ob.url}
          disabled=${ob.starting}
          onInput=${(e: Event) => actions.setOnboardUrl((e.target as HTMLInputElement).value)}
        />
        <button type="submit" class="btn" disabled=${ob.starting || !ob.url.trim()}>
          ${ob.starting ? 'Queuing…' : 'Start crawl'}
        </button>
      </div>
      ${ErrorNote({ message: ob.startError, label: 'The server did not queue it.' })}

      <fieldset class="onb__choices">
        <legend class="unlock__label">Whose site is it?</legend>
        ${Choice({
          value: 'third-party',
          current: ob.ownership,
          head: 'Someone else’s site',
          body: html`The default. Crawled under third-party rules and published with the “Unofficial”
            disclaimer. An account cannot publish it: a stranger’s brand goes on this host only through
            the operator, once the brand’s permission is on record.`,
          onPick: actions.setOnboardOwnership,
        })}
        ${Choice({
          value: 'self-owned',
          current: ob.ownership,
          head: 'My own site',
          body: html`No disclaimer. Before it can be published the domain has to be proved yours — a DNS
            TXT record, a well-known file or a meta tag — and the server refuses${SP}
            <code>ownership_unproven</code> until one passes. The proof is checked at publish time,
            not now.`,
          onPick: actions.setOnboardOwnership,
        })}
        ${Choice({
          value: 'permitted',
          current: ob.ownership,
          head: 'Permitted by the brand',
          body: html`The brand’s owner has agreed in writing. Published without the disclaimer, and
            gated exactly like your own site: an account publishes it only with a verified claim on
            the domain; otherwise the operator publishes it once the permission is on file.`,
          onPick: actions.setOnboardOwnership,
        })}
      </fieldset>
      ${operator
        ? html`<p class="onb__note">
            As the operator you can publish any draft. A self-owned or permitted draft with no verified
            claim is published with a warning in the server log — or refused, on a host that sets${SP}
            <code>CLAWHARBOR_OWNERSHIP_REQUIRE_FOR_PUBLISH=true</code>.
          </p>`
        : html`<p class="onb__note">
            The crawl is recorded against your account, which is what lets you publish it yourself
            — for your own domain, once it is verified. Nothing is served until you press Publish.
          </p>`}
    </form>
  `;
}

/* ── The job ───────────────────────────────────────────────────────────── */

function Progress(props: { job: OnboardJob; ob: OnboardState; actions: DashboardActions; now: number }): VNode {
  const { job, ob, actions, now } = props;
  const live = job.state === 'queued' || job.state === 'running';
  const tail = job.log.slice(-12);

  return html`
    <div class="jobcard">
      <div class="jobcard__top">
        <span class="jobcard__url"><code>${job.url}</code></span>
        ${Pill({ tone: stateTone(job.state, Boolean(job.publishedAt)), children: stateWord(job) })}
        ${Pill({ children: ownershipWord(job.ownership) })}
      </div>
      <p class="jobcard__meta">
        Tenant id <code>${job.tenantId}</code> · queued${SP}
        ${When({ iso: job.createdAt, now, absent: 'at an unknown time' })}
        ${job.finishedAt ? html`${SP}· finished ${When({ iso: job.finishedAt, now, absent: '' })}` : null}
        ${job.capabilities !== null ? html`${SP}· ${job.capabilities} ${job.capabilities === 1 ? 'capability' : 'capabilities'}` : null}
      </p>

      ${live
        ? html`<div class="jobcard__bar" role="status" aria-live="polite">
            <progress class="jobcard__progress" max="100" value=${job.progress}></progress>
            <span class="jobcard__phase">${job.phase || job.state}${SP}· ${job.progress}%</span>
          </div>`
        : null}

      ${job.state === 'failed'
        ? ErrorNote({ message: job.error ?? 'the server recorded no reason', label: 'The crawl failed.' })
        : null}
      ${job.state === 'cancelled'
        ? html`<p class="cancelled" role="status">The crawl was cancelled. Nothing was published.</p>`
        : null}
      ${ErrorNote({ message: ob.jobError, label: 'Could not read the job.' })}

      ${tail.length
        ? html`<details class="devs" open=${live}>
            <summary class="devs__summary">Log · ${job.log.length} ${job.log.length === 1 ? 'line' : 'lines'}</summary>
            ${Scroller({
              label: 'Crawl log',
              children: html`<pre class="jobcard__log">${tail.join('\n')}</pre>`,
            })}
          </details>`
        : null}

      ${live
        ? html`<div class="jobcard__actions">
            <button
              type="button"
              class="btn btn--ghost"
              disabled=${ob.cancelling}
              onClick=${() => actions.cancelCrawl()}
            >
              ${ob.cancelling ? 'Cancelling…' : 'Cancel the crawl'}
            </button>
          </div>`
        : null}
    </div>
  `;
}

/**
 * The next step, beside a refusal the server keyed with a code.
 *
 * The three proofs and their verification are the ownership routes' own; this
 * block names them and points at the two places that walk them — the public
 * console, which shows the token and the three proofs copy-pasteably, and
 * the API. It restates nothing the server's sentence already said.
 */
function OwnershipSteps(props: { code: string; draft: DraftSummary | null; job: OnboardJob }): VNode | null {
  const { code, draft, job } = props;
  if (code !== 'ownership_unproven' && code !== 'third_party_requires_operator') return null;
  const origin = draft?.origin ?? job.url;
  const domain = hostOf(origin);
  return html`
    <div class="handoff">
      <h3 class="handoff__h">Prove the domain, then publish again</h3>
      <ol class="steps">
        <li class="step">
          Ask for a challenge: <code>POST /api/ownership/${job.tenantId}/challenge</code> with${SP}
          <code>{"domain":"${domain}"}</code>. The answer carries, once, a token and three proofs: a DNS
          TXT record, a well-known file, and a meta tag.
        </li>
        <li class="step">Put any one of them on ${domain}.</li>
        <li class="step">
          Verify: <code>POST /api/ownership/${job.tenantId}/verify</code>. A miss answers${SP}
          <code>verified:false</code> with the reason; a hit records the claim.
        </li>
        <li class="step">
          ${code === 'third_party_requires_operator'
            ? html`Crawl the site again as <b>My own site</b> — this draft asserts third-party and the
                server publishes it only through the operator — then press Publish on the new draft.`
            : html`Press <b>Publish</b> here again.`}
        </li>
      </ol>
      <p class="handoff__why">
        The public console at <a href="/">${location.host}</a> walks the same three proofs with copy
        buttons after a crawl of the site; the record it verifies is the same one this host checks.
      </p>
    </div>
  `;
}

function Draft(props: { state: DashboardState; actions: DashboardActions; job: OnboardJob }): VNode {
  const { state, actions, job } = props;
  const ob = state.onboard;
  const draft = ob.draft;
  const published = ob.published;

  if (published) {
    return html`
      <div class="pubcard" role="status">
        <h2 class="sec__h pubcard__h">Published${published.displayName ? html` — ${published.displayName}` : null}</h2>
        <p class="sec__p">
          ${published.replaced
            ? html`A tenant with the id <code>${published.tenantId}</code> was already live and has been replaced.`
            : html`The tenant <code>${published.tenantId}</code> is live.`}
          ${SP}${published.capabilities.length} ${published.capabilities.length === 1 ? 'tool' : 'tools'}
          ${published.ownership ? html`, published as ${ownershipWord(published.ownership)}` : null}.
        </p>
        <div class="urls">
          <div class="url"><span class="url__tag">page</span><a class="url__value" href=${published.urls.web}>${published.urls.web}</a></div>
          <div class="url"><span class="url__tag">mcp</span><code class="url__value">${published.urls.mcp}</code></div>
        </div>
        <div class="jobcard__actions">
          <a class="btn" href=${`#/sites/${encodeURIComponent(published.tenantId)}`}>Open the site</a>
          <a class="btn btn--ghost" href=${`#/sites/${encodeURIComponent(published.tenantId)}/connect`}>Connect a client</a>
        </div>
      </div>
    `;
  }

  if (job.publishedAt) {
    return html`
      <div class="pubcard" role="status">
        <h2 class="sec__h pubcard__h">Published</h2>
        <p class="sec__p">
          This draft went live${SP}
          ${When({ iso: job.publishedAt, now: Date.now(), absent: '' })} as <code>${job.tenantId}</code>.
        </p>
        <div class="jobcard__actions">
          <a class="btn" href=${`#/sites/${encodeURIComponent(job.tenantId)}`}>Open the site</a>
        </div>
      </div>
    `;
  }

  if (ob.draftError) {
    return html`
      <h2 class="sec__h">The draft</h2>
      ${ErrorNote({ message: ob.draftError, label: 'The draft cannot be read, so it cannot be published.' })}
    `;
  }

  if (!draft) {
    return html`<h2 class="sec__h">The draft</h2><p class="sec__p">Reading the draft manifest…</p>`;
  }

  const caps = draft.capabilities;
  const outcome = readCrawlOutcome(job.log);
  const registered = caps.filter((c) => c.register).length;
  // The gate's own row count per tool. A draft table that lists a tool without
  // saying what it returned is the claim without the evidence, and the evidence
  // is already in the log this screen is polling.
  const rowsByTool = new Map(outcome.executed.map((v) => [v.id, v]));

  return html`
    <h2 class="sec__h">The draft${SP}<span class="onb__name">${draft.displayName}</span></h2>
    <p class="sec__p">
      From <code>${draft.origin}</code>, assembled as ${ownershipWord(draft.ownership)}.${SP}
      ${caps.length} ${caps.length === 1 ? 'tool' : 'tools'}
      ${registered !== caps.length
        ? html`, of which ${registered} ${registered === 1 ? 'is' : 'are'} switched on`
        : null}
      ${draft.views.length ? html`${SP}and ${draft.views.length} ${draft.views.length === 1 ? 'view' : 'views'}` : null}.
      Nothing below is served yet.
    </p>
    ${GateLedger({ outcome, caps })}

    ${caps.length
      ? Scroller({
          label: 'Draft tools',
          children: html`<table class="drafttable">
            <thead>
              <tr>
                <th scope="col">Tool</th>
                <th scope="col">Kind</th>
                <th scope="col">Title</th>
                <th scope="col">View</th>
                <th scope="col" class="num">Rows at the gate</th>
                <th scope="col">Served</th>
              </tr>
            </thead>
            <tbody>
              ${caps.map(
                (c) => html`<tr class=${c.register ? undefined : 'drafttable__row--off'}>
                  <td><code>${c.name}</code></td>
                  <td>${c.kind ? html`<span class=${c.kind === 'stage' ? 'kind kind--stage' : 'kind'}>${c.kind}</span>` : '—'}</td>
                  <td>${c.title}</td>
                  <td>${c.view ? html`<code>${c.view}</code>` : '—'}</td>
                  <td class="num">
                    ${(() => {
                      const v = rowsByTool.get(c.name);
                      if (!v) return html`<span class="caps--none">not in the log</span>`;
                      return html`<span title=${`${Math.round(v.passRate * 100)}% of samples passed`}
                        >${v.rows}</span
                      >`;
                    })()}
                  </td>
                  <td>
                    ${c.register
                      ? html`<span class="served">on</span>`
                      : html`<span
                          class="served served--off"
                          title=${c.usefulness
                            ? `score ${c.usefulness.score} against a threshold of ${c.usefulness.threshold}`
                            : 'ranked below this run’s usefulness threshold'}
                          >off</span
                        >`}
                  </td>
                </tr>`,
              )}
            </tbody>
          </table>`,
        })
      : Empty({
          title: 'The crawl produced no tools.',
          body: 'The server refuses to publish a draft with none, and so does the button below. A site whose information lives behind a search form or in a PDF is the usual reason; another crawl of a deeper page is the usual fix.',
        })}

    ${CrawlRefusals({ outcome, caps, origin: draft.origin })}

    <div class="jobcard__actions">
      <button
        type="button"
        class="btn"
        disabled=${ob.publishing || !caps.length}
        onClick=${() => actions.publishDraft()}
      >
        ${ob.publishing ? 'Publishing…' : 'Publish'}
      </button>
    </div>
    ${ob.publishError
      ? html`
          ${ErrorNote({
            message: ob.publishError.code
              ? `${ob.publishError.message} (${ob.publishError.code})`
              : ob.publishError.message,
            label: 'The server refused to publish.',
          })}
          ${ob.publishError.code === 'unknown_job'
            ? html`<p class="sec__p">
                The server answers 404 for a job it does not know and for a job another account
                submitted; the two read the same on purpose. As the operator, publish it with the
                secret instead.
              </p>`
            : null}
          ${OwnershipSteps({ code: ob.publishError.code, draft, job })}
        `
      : null}
  `;
}

/* ── Recent jobs ───────────────────────────────────────────────────────── */

/**
 * The run before this one, for the same tenant.
 *
 * The list arrives newest first, so "before" is further down it. This is the
 * only comparison the screen can make without a second request, and it is the
 * one that matters: the same site crawled three times in a day produced nine,
 * six and four capabilities, and nothing anywhere said so — the count was on
 * the job record all along and no column printed it.
 *
 * "Before" means before IN THIS LIST, which is the last 25 crawls the account
 * can see, and the delta's own tooltip says so rather than claiming a history
 * this screen has not been given.
 */
function previousRun(jobs: OnboardJob[], index: number): OnboardJob | null {
  const mine = jobs[index];
  if (!mine) return null;
  for (let i = index + 1; i < jobs.length; i += 1) {
    const other = jobs[i];
    if (other && other.tenantId === mine.tenantId && other.capabilities !== null) return other;
  }
  return null;
}

/**
 * How many capabilities this run produced, against the run before it.
 *
 * A crawl is not deterministic — the page budget, the site's own latency and
 * what its ranker reached all move the number — so the delta is stated rather
 * than smoothed away. Null is "the crawl never got that far", printed as an em
 * dash rather than as a zero somebody would read as "this site has nothing".
 */
function Capabilities(props: { job: OnboardJob; previous: OnboardJob | null }): VNode {
  const n = props.job.capabilities;
  if (n === null) return html`<span class="caps caps--none">—</span>`;
  const before = props.previous?.capabilities ?? null;
  const delta = before === null ? 0 : n - before;
  return html`<span class="caps"
    >${n}${delta !== 0
      ? html`<span class=${delta < 0 ? 'caps__delta caps__delta--down' : 'caps__delta'}
          title=${`the run before it in this list — ${props.job.tenantId} — produced ${String(before)}`}
          >${delta > 0 ? `+${delta}` : String(delta)}</span
        >`
      : null}</span
  >`;
}

function Recent(props: { state: DashboardState; actions: DashboardActions; now: number }): VNode {
  const { state, actions, now } = props;
  const ob = state.onboard;
  const jobs = ob.jobs;
  const operator = state.principal?.kind === 'operator';
  const heading = operator ? 'All crawls on this host' : 'Your crawls';

  return html`
    <h2 class="sec__h">${heading}</h2>
    ${operator
      ? html`<p class="sec__p">
          Every crawl submitted to this host, whoever started it. Opening
          one polls its record; publishing it still needs to be allowed by the server.
        </p>`
      : html`<p class="sec__p">
          The crawls you started from this account. Nobody else's appear here, and a crawl started
          before you signed in was recorded without an account and stays with the operator. Opening
          one polls its record; publishing it still needs to be allowed by the server.
        </p>`}
    ${ErrorNote({ message: ob.jobsError, label: 'Could not load the list.' })}
    ${jobs === null
      ? ob.jobsError
        ? null
        : html`<p class="sec__p">Loading…</p>`
      : jobs.length === 0
        ? Empty({
            title: operator
              ? 'No crawl has been submitted to this host yet.'
              : 'You have not started a crawl yet.',
            body: operator
              ? 'Every crawl anybody starts on this host appears here, finished or not, for the last 25 of them.'
              : 'Paste an address in the form above to begin one. It appears in this list while it runs, so a reload lands back on it rather than on an empty form.',
          })
        : Scroller({
            label: heading,
            children: html`<table class="jobs">
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Tenant</th>
                  <th scope="col">State</th>
                  <th scope="col" class="num">Capabilities</th>
                  <th scope="col">Ownership</th>
                  <th scope="col">Queued</th>
                  <th scope="col"><span class="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                ${jobs.map(
                  (j, i) => html`<tr class=${j.id === ob.jobId ? 'jobs__row jobs__row--on' : 'jobs__row'}>
                    <td class="jobs__url"><code>${j.url}</code></td>
                    <td><code>${j.tenantId}</code></td>
                    <td>${Pill({ tone: stateTone(j.state, Boolean(j.publishedAt)), children: stateWord(j) })}</td>
                    <td class="num">${Capabilities({ job: j, previous: previousRun(jobs, i) })}</td>
                    <td>${ownershipWord(j.ownership)}</td>
                    <td>${When({ iso: j.createdAt, now, absent: '—' })}</td>
                    <td>
                      <a class="linkish" href=${`#/crawl/${encodeURIComponent(j.id)}`} onClick=${(e: Event) => {
                        e.preventDefault();
                        actions.openJob(j.id);
                      }}>
                        ${j.id === ob.jobId ? 'Open' : j.draft ? 'Review' : 'Resume'}
                      </a>
                    </td>
                  </tr>`,
                )}
              </tbody>
            </table>`,
          })}
  `;
}

/* ── The screen ────────────────────────────────────────────────────────── */

export function OnboardScreen(props: { state: DashboardState; actions: DashboardActions; now: number }): VNode {
  const { state, actions, now } = props;
  const ob = state.onboard;
  const job = ob.job;

  return html`
    <section class="wrap screen">
      <h1 class="screen__h" tabindex="-1" data-focus-target>
        ${ob.jobId ? (job ? hostOf(job.url) : 'Crawl') : 'Add a site'}
      </h1>
      ${ob.jobId
        ? html`
            <p class="screen__p">
              ${job
                ? job.publishedAt
                  ? 'This crawl has been published.'
                  : job.draft
                    ? 'The crawl finished. What it found is a draft; review it below, then publish it.'
                    : job.state === 'queued' || job.state === 'running'
                      ? 'The crawler is reading the site. This page re-reads the job every few seconds; you can leave and come back.'
                      : 'The crawl ended without a draft.'
                : ob.jobError
                  ? 'The job could not be read.'
                  : 'Reading the job…'}
              ${SP}<button type="button" class="linkish" onClick=${() => actions.newCrawl()}>Start another crawl</button>
            </p>
            ${job
              ? html`${Progress({ job, ob, actions, now })}${job.state === 'succeeded' ? Draft({ state, actions, job }) : null}`
              : ErrorNote({ message: ob.jobError, label: 'Could not read the job.' })}
          `
        : html`
            <p class="screen__p">
              Paste an address and the crawler reads the site — its sitemap, its structured data, the
              pages people actually use — and assembles read-only tools from what it can verify. The
              result is a draft. Nothing is served until you review it and press Publish.
            </p>
            ${CrawlForm({ state, actions })}
          `}

      ${Recent({ state, actions, now })}

      <p class="screen__foot">
        A crawl asks the site’s <code>robots.txt</code> first and refuses what it disallows. A draft
        that goes out as someone else’s site carries the “Unofficial” disclaimer and is published only
        by the operator with the brand’s permission; a draft that goes out as your own carries none,
        and the server asks for proof of the domain before it lets that happen. No tool a crawl
        produces can write, book, pay or sign in on anybody’s behalf.
      </p>
    </section>
  `;
}
